// src/components/analytics/AnalyticsLiveFeed.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — "Live AI activity" drawer for the Analytics Hub.
// Shows what the AI did, newest first: model promotions / rollbacks, training outcomes,
// prediction batches, critical risks, anomalies, flood risk, analysis runs.
//
// Props: isOpen, onClose, feed (from useAnalyticsLive), status, loading, error,
//        onRefresh, onNavigate(path)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import {
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  Box, Flex, HStack, VStack, Text, Badge, Button, IconButton, Icon, Tooltip, Skeleton,
  Alert, AlertIcon, useColorModeValue,
} from '@chakra-ui/react';
import {
  MdBolt, MdRefresh, MdErrorOutline, MdWarningAmber, MdInfoOutline, MdCheckCircleOutline,
  MdChevronRight,
} from 'react-icons/md';
import { timeAgo } from '../ml/mlFormat';

const SEV = {
  critical: { color: 'red', icon: MdErrorOutline },
  warning: { color: 'orange', icon: MdWarningAmber },
  info: { color: 'blue', icon: MdInfoOutline },
  success: { color: 'green', icon: MdCheckCircleOutline },
};
const FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'attention', label: 'Needs attention', test: (e) => e.severity === 'critical' || e.severity === 'warning' },
  { key: 'models', label: 'Models', test: (e) => /^(model_|training_)/.test(e.kind) || e.kind === 'cache_reset' },
  { key: 'predictions', label: 'Predictions & risk', test: (e) => ['prediction_batch', 'risk_critical', 'analysis_complete', 'flood_risk', 'anomaly'].includes(e.kind) },
];
export const STATUS_META = {
  connected: { color: 'green', label: 'Live' },
  connecting: { color: 'yellow', label: 'Connecting' },
  reconnecting: { color: 'orange', label: 'Reconnecting' },
  waiting: { color: 'red', label: 'Offline — retrying' },
  disconnected: { color: 'red', label: 'Disconnected' },
  idle: { color: 'gray', label: 'Not connected' },
};

export default function AnalyticsLiveFeed({ isOpen, onClose, feed = [], status = 'idle', loading = false, error = null, onRefresh, onNavigate }) {
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const border = useColorModeValue('gray.100', 'whiteAlpha.100');
  const [filter, setFilter] = useState('all');

  const rows = useMemo(() => {
    const f = FILTERS.find(x => x.key === filter) || FILTERS[0];
    return feed.filter(f.test);
  }, [feed, filter]);
  const st = STATUS_META[status] || STATUS_META.idle;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <HStack spacing={2}>
            <Icon as={MdBolt} color="purple.400" />
            <Text>Live AI activity</Text>
            <Badge colorScheme={st.color} variant="subtle" fontSize="2xs">{st.label}</Badge>
          </HStack>
          <HStack mt={3} spacing={1} flexWrap="wrap">
            {FILTERS.map(f => (
              <Button key={f.key} size="xs" variant={filter === f.key ? 'solid' : 'ghost'} colorScheme={filter === f.key ? 'purple' : 'gray'} onClick={() => setFilter(f.key)}>
                {f.label}
              </Button>
            ))}
            <Box flex="1" />
            <Tooltip label="Reload from the server" hasArrow>
              <IconButton size="xs" variant="ghost" aria-label="Reload activity" icon={<MdRefresh />} isLoading={loading} onClick={onRefresh} />
            </Tooltip>
          </HStack>
        </DrawerHeader>
        <DrawerBody px={2} py={2}>
          {error && (
            <Alert status="warning" borderRadius="md" mb={2} fontSize="sm"><AlertIcon />Activity history could not be loaded: {error.message}</Alert>
          )}
          {loading && feed.length === 0 ? (
            <VStack align="stretch" spacing={2} p={2}>{[0, 1, 2, 3, 4].map(i => <Skeleton key={i} h="48px" borderRadius="md" />)}</VStack>
          ) : rows.length === 0 ? (
            <Text fontSize="sm" color={muted} p={4} textAlign="center">
              {feed.length ? 'Nothing in this filter yet.' : 'No AI activity yet. Model changes, predictions and risks appear here as they happen.'}
            </Text>
          ) : (
            <VStack align="stretch" spacing={0} data-testid="live-feed">
              {rows.map((e) => {
                const s = SEV[e.severity] || SEV.info;
                return (
                  <Flex key={`${e.id || ''}${e.at}${e.title}`} gap={3} px={3} py={2.5} borderBottomWidth="1px" borderColor={border} _hover={{ bg: rowHover }} align="flex-start">
                    <Icon as={s.icon} color={`${s.color}.400`} mt="3px" flexShrink={0} />
                    <Box flex="1" minW={0}>
                      <Text fontSize="sm" fontWeight="semibold">{e.title}</Text>
                      {e.detail && <Text fontSize="xs" color={muted}>{e.detail}</Text>}
                      <HStack spacing={2} mt={1}>
                        <Text fontSize="2xs" color={muted}>{timeAgo(e.at)}</Text>
                        {e.modelType && <Badge fontSize="2xs" variant="outline">{String(e.modelType).replace(/_/g, ' ').toLowerCase()}</Badge>}
                      </HStack>
                    </Box>
                    {e.link && onNavigate && (
                      <IconButton size="xs" variant="ghost" aria-label="Open" icon={<MdChevronRight />} onClick={() => onNavigate(e.link)} />
                    )}
                  </Flex>
                );
              })}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
